/**
 * LES PRÉVISIONS — lignes prévues et rapprochement (lot 98, 18/09/2026).
 *
 * Cadrage : « Analyse financière : 3. les prévisions » et « 8. les
 * arbitrages ». Partagé par l'écran Prévisions, le BFR et la trésorerie.
 *
 * Une prévision (contrat ou estimation) produit une ligne par date de son
 * rythme. Les lignes ne sont pas stockées ; seuls les écarts le sont :
 * décalage, suppression, rapprochement.
 */

import { ajouterJours, ecartJours } from './echeancier.js';

export type Rythme = 'hebdo' | 'mensuel' | 'trimestriel' | 'une_fois';

export interface Prevision {
  readonly id: string;
  readonly cote: 'client' | 'fournisseur';
  readonly type: 'contrat' | 'estimation';
  readonly tiers_pennylane_id: string | null;
  readonly tiers_nom: string;
  readonly montant_ht_centimes: number;
  readonly taux_tva: number;
  readonly rythme: Rythme;
  /** 0 = lundi … 6 = dimanche. */
  readonly jour_semaine: number | null;
  readonly jour_mois: number | null;
  readonly fin_de_mois: boolean;
  readonly mois_depart: number | null;
  readonly du: string;
  readonly au: string | null;
  readonly delai_jours: number;
  readonly prelevement: boolean;
  readonly actif: boolean;
  readonly repartition: '%' | '€';
  /**
   * Jour de création. Les dates du rythme ANTÉRIEURES ne produisent pas de
   * ligne : leurs pièces existent déjà (ou non) et sont comptées ailleurs —
   * les générer ferait compter deux fois le passé. Nul : pas de limite.
   */
  readonly cree_le: string | null;
  /**
   * Clé : identifiant d'activité, ou `''` pour la structure. Valeurs ENTIÈRES :
   * centièmes de point en mode % (5 000 = 50 %), centimes en mode €.
   */
  readonly parts: Readonly<Record<string, number>>;
}

export interface EcartDeLigne {
  readonly date_rythme: string;
  readonly decalee_au: string | null;
  readonly supprimee: boolean;
}

export interface LignePrevue {
  readonly prevision_id: string;
  /** Identifiant stable d'une ligne : prévision + date du rythme. */
  readonly cle: string;
  readonly date_rythme: string;
  /** Date de la pièce attendue (décalée le cas échéant). */
  readonly date: string;
  readonly decalee: boolean;
  readonly echeance: string;
  /** Date du règlement prévu. */
  readonly reglement: string;
  readonly ttc_centimes: number;
  readonly ht_centimes: number;
  readonly passee: boolean;
}

/** Les réglages du rapprochement (doc 3 et doc 8). */
export const REGLAGES_RAPPROCHEMENT = {
  toleranceContrat: 5,
  toleranceEstimation: 30,
  fenetreHebdo: 3,
  fenetreMois: 10,
} as const;

/** Nombre de semaines générées devant aujourd'hui. */
export const HORIZON_SEMAINES = 13;

export function ttcCentimes(ht_centimes: number, taux: number): number {
  return Math.round(ht_centimes * (1 + taux / 100));
}

function dernierJour(annee: number, mois0: number): number {
  return new Date(Date.UTC(annee, mois0 + 1, 0)).getUTCDate();
}

function iso(annee: number, mois0: number, jour: number): string {
  return new Date(Date.UTC(annee, mois0, jour)).toISOString().slice(0, 10);
}

/**
 * LES DATES DU RYTHME entre deux bornes incluses.
 *
 * Un jour du mois au-delà de la fin du mois (le 31 en février) tombe au
 * DERNIER jour du mois — la maquette ne traitait pas ce cas ; c'est la règle
 * des prélèvements bancaires.
 */
export function datesDuRythme(p: Prevision, depuis: string, jusqua: string): string[] {
  const debut = p.du > depuis ? p.du : depuis;
  const fin = p.au !== null && p.au < jusqua ? p.au : jusqua;
  if (debut > fin) return [];
  if (p.rythme === 'une_fois') return p.du >= debut && p.du <= fin ? [p.du] : [];
  if (p.rythme === 'hebdo') {
    const js = (new Date(`${debut}T00:00:00Z`).getUTCDay() + 6) % 7;
    const res: string[] = [];
    for (
      let d = ajouterJours(debut, ((p.jour_semaine ?? 0) - js + 7) % 7);
      d <= fin;
      d = ajouterJours(d, 7)
    ) {
      res.push(d);
    }
    return res;
  }
  const res: string[] = [];
  const annee = Number(debut.slice(0, 4));
  const mois0 = Number(debut.slice(5, 7)) - 1;
  for (let k = 0; k < 240; k += 1) {
    const m = mois0 + k;
    const a = annee + Math.floor(m / 12);
    const mm = ((m % 12) + 12) % 12;
    if (p.rythme === 'trimestriel' && (mm + 1 - (p.mois_depart ?? 1) + 12) % 3 !== 0)
      continue;
    const jour = p.fin_de_mois
      ? dernierJour(a, mm)
      : Math.min(p.jour_mois ?? 1, dernierJour(a, mm));
    const d = iso(a, mm, jour);
    if (d > fin) break;
    if (d >= debut) res.push(d);
  }
  return res;
}

/**
 * LES LIGNES PRÉVUES d'un ensemble de prévisions.
 *
 * `retards` : retard habituel par tiers Pennylane (clients seulement,
 * jamais négatif — même règle que l'échéancier). `rapprochees` : clés des
 * lignes déjà rapprochées d'une pièce, qui ne sont plus des prévisions.
 * Une ligne passée d'une prévision sans tiers Pennylane n'est pas rendue.
 */
export function lignesPrevues(
  previsions: readonly Prevision[],
  ecarts: ReadonlyMap<string, EcartDeLigne>,
  rapprochees: ReadonlySet<string>,
  retards: ReadonlyMap<string, number | null>,
  depuis: string,
  jusqua: string,
  aujourdhui: string,
): LignePrevue[] {
  const res: LignePrevue[] = [];
  for (const p of previsions) {
    if (!p.actif) continue;
    for (const d of datesDuRythme(p, depuis, jusqua)) {
      const cle = `${p.id}|${d}`;
      if (p.cree_le !== null && d < p.cree_le) continue;
      if (rapprochees.has(cle)) continue;
      const e = ecarts.get(cle);
      if (e?.supprimee === true) continue;
      const date = e?.decalee_au ?? d;
      // UNE LIGNE PASSÉE SANS TIERS PENNYLANE est réputée faite : aucune
      // pièce ne viendra la solder (salaires, charges sociales…), et la
      // garder la ferait compter indéfiniment dans les prévisions.
      if (p.tiers_pennylane_id === null && date < aujourdhui) continue;
      const echeance = ajouterJours(date, p.delai_jours);
      const retard =
        p.cote === 'client' && p.tiers_pennylane_id !== null
          ? Math.max(0, retards.get(p.tiers_pennylane_id) ?? 0)
          : 0;
      res.push({
        prevision_id: p.id,
        cle,
        date_rythme: d,
        date,
        decalee: date !== d,
        echeance,
        reglement: ajouterJours(echeance, retard),
        ttc_centimes: ttcCentimes(p.montant_ht_centimes, p.taux_tva),
        ht_centimes: p.montant_ht_centimes,
        passee: date < aujourdhui,
      });
    }
  }
  return res.sort((a, b) =>
    a.reglement < b.reglement ? -1 : a.reglement > b.reglement ? 1 : 0,
  );
}

/** La part d'une ligne qui revient à une activité (`''` : structure). */
export function partDe(p: Prevision, activite: string): number {
  const v = p.parts[activite] ?? 0;
  if (p.repartition === '%') return v / 10000;
  return p.montant_ht_centimes === 0 ? 0 : v / p.montant_ht_centimes;
}

/**
 * CONTRÔLE DE LA RÉPARTITION : le total doit tomber juste — 100 % ou le
 * montant HT exact (doc 3). En %, les parts sont en centièmes de point
 * (5 000 = 50 %) ; en €, en centimes. Rend la phrase du refus, ou nul.
 */
export function refusDeRepartition(
  mode: '%' | '€',
  parts: readonly number[],
  montant_ht_centimes: number,
): string | null {
  if (parts.length === 0) return 'Répartition : indiquez au moins une activité.';
  if (parts.some((x) => !Number.isInteger(x) || x <= 0)) {
    return 'Répartition : chaque part doit être positive.';
  }
  const total = parts.reduce((s, x) => s + x, 0);
  if (mode === '%') {
    return total === 10000
      ? null
      : `Répartition : le total fait ${(total / 100).toLocaleString('fr-FR')} %, il doit faire 100 %.`;
  }
  if (total === montant_ht_centimes) return null;
  const ecart = Math.abs(montant_ht_centimes - total);
  const e = (c: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
      .format(c / 100)
      .replace(/\s/g, ' ');
  return total < montant_ht_centimes
    ? `Répartition : le total fait ${e(total)} sur ${e(montant_ht_centimes)} HT, il manque ${e(ecart)}.`
    : `Répartition : le total fait ${e(total)} sur ${e(montant_ht_centimes)} HT, ${e(ecart)} de trop.`;
}

/** Une vraie pièce, candidate au rapprochement. */
export interface PieceReelle {
  readonly pennylane_id: string;
  readonly tiers_pennylane_id: string | null;
  readonly numero: string | null;
  readonly date: string;
  readonly ttc_centimes: number;
}

export interface Candidat {
  readonly piece: PieceReelle;
  readonly ecart_centimes: number;
  /** Écart relatif (−0,088 pour −8,8 %). */
  readonly relatif: number;
  readonly dans_tolerance: boolean;
  readonly jours: number;
}

/** Les pièces candidates pour une ligne, les meilleures d'abord. */
export function candidatsDe(
  ligne: LignePrevue,
  p: Prevision,
  pieces: readonly PieceReelle[],
  refus: ReadonlySet<string>,
): Candidat[] {
  if (p.tiers_pennylane_id === null) return [];
  const fenetre =
    p.rythme === 'hebdo'
      ? REGLAGES_RAPPROCHEMENT.fenetreHebdo
      : REGLAGES_RAPPROCHEMENT.fenetreMois;
  const tolerance =
    (p.type === 'contrat'
      ? REGLAGES_RAPPROCHEMENT.toleranceContrat
      : REGLAGES_RAPPROCHEMENT.toleranceEstimation) / 100;
  return pieces
    .filter(
      (x) =>
        x.tiers_pennylane_id === p.tiers_pennylane_id &&
        Math.abs(ecartJours(ligne.date, x.date)) <= fenetre &&
        !refus.has(`${ligne.cle}|${x.pennylane_id}`),
    )
    .map((x) => {
      const ecart = x.ttc_centimes - ligne.ttc_centimes;
      const relatif = ligne.ttc_centimes === 0 ? 0 : ecart / ligne.ttc_centimes;
      return {
        piece: x,
        ecart_centimes: ecart,
        relatif,
        dans_tolerance: Math.abs(relatif) <= tolerance + 1e-9,
        jours: ecartJours(ligne.date, x.date),
      };
    })
    .sort(
      (a, b) =>
        Math.abs(a.relatif) - Math.abs(b.relatif) ||
        Math.abs(a.jours) - Math.abs(b.jours),
    );
}

export interface Proposition {
  readonly ligne: LignePrevue;
  readonly meilleur: Candidat;
  readonly autres: readonly Candidat[];
}

export interface Propositions {
  readonly a_confirmer: readonly Proposition[];
  /** Lignes passées depuis 3 jours au moins, sans pièce : décaler ou supprimer. */
  readonly sans_piece: readonly LignePrevue[];
}

/**
 * LES PROPOSITIONS, maquette v2 : chaque pièce ne va qu'à une ligne, les
 * meilleurs couples d'abord. Seules les lignes datées jusqu'à aujourd'hui
 * + 10 jours sont proposées. Une ligne sans tiers Pennylane n'est jamais
 * « sans pièce » : elle n'en aura pas (salaires…).
 */
export function propositions(
  lignes: readonly LignePrevue[],
  previsions: ReadonlyMap<string, Prevision>,
  pieces: readonly PieceReelle[],
  refus: ReadonlySet<string>,
  aujourdhui: string,
): Propositions {
  const limite = ajouterJours(aujourdhui, 10);
  const retenues = lignes.filter((l) => l.date <= limite);
  const couples: { ligne: LignePrevue; c: Candidat; p: Prevision }[] = [];
  for (const l of retenues) {
    const p = previsions.get(l.prevision_id);
    if (p === undefined) continue;
    for (const c of candidatsDe(l, p, pieces, refus)) couples.push({ ligne: l, c, p });
  }
  couples.sort((a, b) => Math.abs(a.c.relatif) - Math.abs(b.c.relatif));
  const prisesL = new Set<string>();
  const prisesP = new Set<string>();
  const res = new Map<
    string,
    { ligne: LignePrevue; meilleur: Candidat; p: Prevision }
  >();
  for (const { ligne, c, p } of couples) {
    if (prisesL.has(ligne.cle) || prisesP.has(c.piece.pennylane_id)) continue;
    prisesL.add(ligne.cle);
    prisesP.add(c.piece.pennylane_id);
    res.set(ligne.cle, { ligne, meilleur: c, p });
  }
  const a_confirmer = [...res.values()]
    .map((x) => ({
      ligne: x.ligne,
      meilleur: x.meilleur,
      autres: candidatsDe(x.ligne, x.p, pieces, refus).filter(
        (a) =>
          a.piece.pennylane_id !== x.meilleur.piece.pennylane_id &&
          !prisesP.has(a.piece.pennylane_id),
      ),
    }))
    .sort((a, b) => (a.ligne.date < b.ligne.date ? -1 : 1));
  const sans_piece = retenues.filter(
    (l) => l.passee && !res.has(l.cle) && ecartJours(l.date, aujourdhui) >= 3,
  );
  return { a_confirmer, sans_piece };
}
